import React, { useState, useRef } from "react"
import styled from "styled-components"
import { Helmet } from "react-helmet-async"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { trackEvent } from "../../../../analytics/agile"
import { pushLeadEvent } from "../../../../utils/analytics"
import { ProfessionalFeatureSection } from "./ProfessionalFeatureSection"
import { ModernStorageSection } from "./ModernStorageSection"


import ReusableHero from "@components/layout/Hero/ReusableHero"
import ReusableServicesOverview from "@components/layout/sections/ReusableServicesOverview"
import { getQuoteFormSchema, renderQuoteForm } from "@components/layout/sections/ReusableCallToAction/Quote"
import ReusableCallToAction from "@components/layout/sections/ReusableCallToAction"
import { Heading } from "@components/base"
import api from "@utils/api.client"

/* ================== Layout de page ================== */
export const Layout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;
  width: 100%;
`

const Section = styled.section<{ bgColor?: string; bgImage?: string }>`
  width: 100%;
  padding: 60px 20px;
  box-sizing: border-box;
  background-color: ${({ bgColor }) => bgColor || "transparent"};
  background-image: ${({ bgImage }) => (bgImage ? `url(${bgImage})` : "none")};
  background-size: cover;
  background-position: center center;
  position: relative;

  @media (min-width: 768px) {
    padding: 80px 40px;
  }
`

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`

const Paragraph = styled.p<{ center?: boolean }>`
  font-size: 1rem;
  line-height: 1.7;
  margin: 2rem 0 1rem;
  color: #333;
  ${({ center }) => center && "text-align: center;"}

  @media (min-width: 768px) {
    font-size: 1.125rem;
  }
`

/* ================== Bloc puces (solutions) ================== */
const BulletList = styled.ul`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  padding: 0;
  margin: 2rem auto;
  max-width: 1000px;
  list-style: none;
  color: #333;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const BulletItem = styled.li`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 1rem 1.5rem;
  font-size: 1.15rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s, box-shadow 0.3s;

  span {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 1rem 0;
    font-size: 1.5rem;
    font-weight: 500;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 8px rgba(0, 0, 0, 0.15);
  }

  .bullet-content {
    position: relative;
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    margin-right: 1rem;
  }
  .bullet-number {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2.5rem;
    font-weight: 500;
    color: ${({ theme }) => theme?.primary || "#007BFF"};
    border-radius: 50%;
    width: 36px;
    height: 36px;
  }
  .bullet-circle {
    position: absolute;
    z-index: 1;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 50px;
    height: 50px;
    background: ${({ theme }) => theme?.primary + 20 || "#007BFF"};
    border-radius: 50%;
    opacity: 0.8;
  }
`

/* ================== Section “Pourquoi nous choisir” (inline) ================== */
const FeatureSection = styled.section`
  padding: 80px 20px;
  background: linear-gradient(135deg, #f9f9f9, #ffffff);
`

const FeatureContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 2rem;

  @media (min-width: 992px) {
    grid-template-columns: repeat(3, 1fr);
    & > :last-child {
      grid-column: 1 / -1; /* s’étend sur toute la largeur */
    }
  }
`

const FeatureCard = styled.div`
  position: relative;
  background: #fff;
  border-radius: 16px;
  padding: 2rem 1.5rem;
  text-align: center;
  overflow: hidden;
  transition: transform 0.3s, box-shadow 0.3s;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
  }
`

const FeatureTitle = styled.h3`
  font-size: 1.8rem;
  margin: 0 0 1rem;
  color: ${({ theme }) => theme?.primary || "#007bff"};
  font-family: "Poppins", sans-serif;
  position: relative;
  z-index: 1;
`

const FeatureDescription = styled.p`
  font-size: 1.2rem;
  color: #666;
  line-height: 1.6;
  position: relative;
  z-index: 1;
`

function ProfessionalFeatureSection({ data }: { data: any }) {
  const features = [data.flexibility, data.security, data.expertise, data.cost]
  return (
    <FeatureSection>
      <FeatureContainer>
        <Heading level={2} center>
          {data.title}
        </Heading>
        <p style={{ textAlign: "center", fontSize: "1.125rem", color: "#555" }}>{data.subheading}</p>
        <FeatureGrid>
          {features.map((feature, i) => (
            <FeatureCard key={i}>
              <FeatureTitle className="gradient-text1">{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.text}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeatureGrid>
      </FeatureContainer>
    </FeatureSection>
  )
}

/* ================== Section “Entreposage” (inline) ================== */
const StorageSection = styled.section`
  padding: 60px 20px;
  background-color: #f5f5f5;
`

const StorageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`

const StorageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;

  @media (min-width: 992px) {
    grid-template-columns: repeat(3, 1fr);
  }
`

const StorageCard = styled.div`
  background: ${({ theme }) => theme?.tertiary || "#fff"};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s;
  &:hover {
    transform: translateY(-5px);
  }
`

const StorageImage = styled.img`
  width: 100%;
  height: 240px;
  object-fit: cover;
`

const StorageContent = styled.div`
  padding: 1rem 1.5rem;
`

const StorageTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: white;
`

const StorageText = styled.p`
  font-size: 1rem;
  color: #b7b7b7;
`

function ModernStorageSection({ data }: { data: any }) {
  const cards = [data.climate, data.inventory, data.vip]
  return (
    <StorageSection>
      <StorageContainer>
        <Heading level={2} center>
          {data.title}
        </Heading>
        <p style={{ textAlign: "center", fontSize: "1.125rem", color: "#555" }}>{data.subheading}</p>
        <StorageGrid>
          {cards.map((card, index) => (
            <StorageCard key={index}>
              <StorageImage src={card.image} alt={card.title} />
              <StorageContent>
                <StorageTitle>{card.title}</StorageTitle>
                <StorageText>{card.text}</StorageText>
              </StorageContent>
            </StorageCard>
          ))}
        </StorageGrid>
      </StorageContainer>
    </StorageSection>
  )
}

/* ================== Page 3PL ================== */
export const Brisson3Pl: React.FC = () => {
  const { t, ready } = useTranslation("services/brisson3pl")
  const navigate = useNavigate()
  const [isPopupVisible] = useState(false)
  const popupTriggered = useRef(false)

  if (!ready) return <div />

  const servicesOverviewData = t("servicesOverview", { returnObjects: true })
  const cardsData = Array.isArray(servicesOverviewData.cards) ? servicesOverviewData.cards : []

  const whyChooseData = t("whyChoose", { returnObjects: true })
  const premiumStorageData = t("premiumStorage", { returnObjects: true })

  const solutionsData = t("solutions", { returnObjects: true })

  const initialValues = { inquiryType: "3PL", name: "", email: "", phoneNumber: "", message: "" }

  const handleSubmit = async (values: typeof initialValues, actions: any) => {
    try {
      const response = await api.post("/inquiries/3pl", values)
      if (response.status === 200 || response.status === 201) {
        // Tracking succès (Agile + dataLayer) AVANT navigation
        trackEvent("DemandeSoumission", { page: window.location.pathname, type: "form" })
        pushLeadEvent("3pl_quote", { page: window.location.pathname })

        actions.resetForm()
        actions.setSubmitting(false)
        setTimeout(() => navigate(t("cta.successRedirectUrl")), 100)
      }
    } catch (error) {
      console.error("Error submitting moving inquiry:", error)
      actions.setSubmitting(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>{t("meta.title")}</title>
        <meta name="description" content={t("meta.description")} />
        <meta name="keywords" content={t("meta.keywords")} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={t("meta.ogTitle")} />
        <meta property="og:description" content={t("meta.ogDescription")} />
        <meta property="og:image" content={t("meta.ogImage")} />
        <meta property="og:url" content={t("meta.ogUrl")} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={t("meta.ogLocale")} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t("meta.twitterTitle")} />
        <meta name="twitter:description" content={t("meta.twitterDescription")} />
        <meta name="twitter:image" content={t("meta.twitterImage")} />
      </Helmet>

      <Layout>
        {/* Hero avec tracking du clic CTA */}
        <ReusableHero
          headingPre={t("hero.headingPre")}
          headingHighlight={t("hero.headingHighlight")}
          headingPost={t("hero.headingPost")}
          subtitle={t("hero.subtitle")}
          imageSrc="/img/3pl-warehouse.jpg"
          imageAlt={t("hero.imageAlt")}
          ctaLabel={t("hero.ctaLabel")}
          ctaOnClick={() => {
            trackEvent("CTA_DemandeSoumission", { page: window.location.pathname, position: "hero_3pl" })
            navigate("/faire-une-demande")
          }}
        />

        <ReusableServicesOverview
          bgImg={"/img/3pl-warehouse4.jpg"}
          hasOverlay
          cardsData={cardsData}
          autoRotateInterval={5000}
        />

        {/* Intro / Solutions */}
        <Section>
          <Container>
            <Heading level={2} center>
              {t("solutionsSection.title")}
            </Heading>
            <Paragraph center>{t("solutionsSection.subheading")}</Paragraph>

            <BulletList>
              <BulletItem>
                <span>
                  <div className="bullet-content">
                    <div className="bullet-number gradient-text1">1</div>
                    <div className="bullet-circle"></div>
                  </div>
                  {t("solutions.transport.title")}
                </span>
                {t("solutions.transport.text")}
              </BulletItem>

              <BulletItem>
                <span>
                  <div className="bullet-content">
                    <div className="bullet-number gradient-text1">2</div>
                    <div className="bullet-circle"></div>
                  </div>
                  {t("solutions.storage.title")}
                </span>
                {t("solutions.storage.text")}
              </BulletItem>

              <BulletItem>
                <span>
                  <div className="bullet-content">
                    <div className="bullet-number gradient-text1">3</div>
                    <div className="bullet-circle"></div>
                  </div>
                  {t("solutions.crating.title")}
                </span>
                {t("solutions.crating.text")}
              </BulletItem>

              <BulletItem>
                <span>
                  <div className="bullet-content">
                    <div className="bullet-number gradient-text1">4</div>
                    <div className="bullet-circle"></div>
                  </div>
                  {t("solutions.customs.title")}
                </span>
                {t("solutions.customs.text")}
              </BulletItem>
            </BulletList>
          </Container>
        </Section>

        {/* Pourquoi nous choisir */}
        <ProfessionalFeatureSection data={whyChooseData} />

        {/* Entreposage */}
        <ModernStorageSection data={premiumStorageData} />

        {/* Formulaire CTA réutilisable */}
        <ReusableCallToAction
          inquiryType="3PL"
          heading={t("cta.heading")}
          text={t("cta.text")}
          initialValues={initialValues}
          validationSchema={getQuoteFormSchema(t)}
          onSubmit={handleSubmit}
          renderForm={(formikProps) => renderQuoteForm(formikProps, t)}
          portraitSrc="/img/cta-persona.png"
          logoMarkSrc="/img/LogoMark.svg"
        />
      </Layout>
    </>
  )
}

export default Brisson3Pl

  
 
           
